export const COMMAND_CENTER_V3_DEFAULT_MAP_STYLE = 'street'
export const LEGACY_DEFAULT_MAP_STYLE = 'light'

/** @deprecated Use COMMAND_CENTER_V3_DEFAULT_MAP_STYLE */
export const V3_DEFAULT_MAP_STYLE = COMMAND_CENTER_V3_DEFAULT_MAP_STYLE

/** @deprecated Gate-off default; use resolveMapStyle({ commandCenterV3Enabled }) */
export const LIVE_SHIPMENT_DEFAULT_MAP_STYLE = LEGACY_DEFAULT_MAP_STYLE

const VALID_MAP_STYLES = new Set(['light', 'street', 'satellite'])

export function isValidMapStyle(value) {
  return typeof value === 'string' && VALID_MAP_STYLES.has(value)
}

export function readStoredMapStyle() {
  if (typeof localStorage === 'undefined') return null
  const stored = localStorage.getItem('mapStyle')
  return isValidMapStyle(stored) ? stored : null
}

export function persistStoredMapStyle(value) {
  if (typeof localStorage === 'undefined' || !isValidMapStyle(value)) return
  localStorage.setItem('mapStyle', value)
}

/**
 * DB column value when set; otherwise normalized context `mapStyle`.
 * @param {string | null | undefined} mapStyleDbValue
 * @param {string | null | undefined} mapStyle
 */
export function coalescePersistedMapStyle(mapStyleDbValue, mapStyle) {
  if (isValidMapStyle(mapStyleDbValue)) return mapStyleDbValue
  if (isValidMapStyle(mapStyle)) return mapStyle
  return null
}

/**
 * Resolve map style for Command Center and Shipment Details when `command-center-v3` is on.
 * Falls back to legacy light default when the gate is off.
 *
 * When `commandCenterV3Enabled`, persisted preference (`savedMapStyle`) wins over
 * `localStorage` so preference-page saves apply on client navigation without refresh.
 *
 * @param {object} options
 * @param {string | null | undefined} options.storedMapStyle - `localStorage.mapStyle`
 * @param {string | null | undefined} options.savedMapStyle - persisted DB/metadata value only (null when unset)
 * @param {boolean} [options.commandCenterV3Enabled] - Statsig `command-center-v3` gate
 */
export function resolveMapStyle({
  storedMapStyle,
  savedMapStyle,
  commandCenterV3Enabled = false,
} = {}) {
  if (commandCenterV3Enabled) {
    if (isValidMapStyle(savedMapStyle)) {
      return savedMapStyle
    }

    if (isValidMapStyle(storedMapStyle)) {
      return storedMapStyle
    }

    return COMMAND_CENTER_V3_DEFAULT_MAP_STYLE
  }

  if (isValidMapStyle(storedMapStyle)) {
    return storedMapStyle
  }

  if (isValidMapStyle(savedMapStyle)) {
    return savedMapStyle
  }

  return LEGACY_DEFAULT_MAP_STYLE
}

/**
 * Resolve map style when `command-center-v3` is on using UserPreferencesContext fields.
 * @param {{ mapStyleDbValue?: string | null, mapStyle?: string | null }} preferences
 */
export function resolveCommandCenterMapStyleFromPreferences({
  mapStyleDbValue = null,
  mapStyle = null,
} = {}) {
  return resolveMapStyle({
    storedMapStyle: readStoredMapStyle(),
    savedMapStyle: coalescePersistedMapStyle(mapStyleDbValue, mapStyle),
    commandCenterV3Enabled: true,
  })
}

/**
 * Reconcile localStorage when persisted preference wins over a stale cache (v3 only).
 * @param {string} resolvedStyle
 */
export function reconcileStoredMapStyleWithResolved(resolvedStyle) {
  const stored = readStoredMapStyle()
  if (isValidMapStyle(resolvedStyle) && resolvedStyle !== stored) {
    persistStoredMapStyle(resolvedStyle)
  }
}

/**
 * @deprecated Use resolveMapStyle with commandCenterV3Enabled
 */
export function resolveShipmentDetailMapStyle({
  storedMapStyle,
  savedMapStyle,
  v3TopPanelLayout = false,
  commandCenterV3Enabled,
} = {}) {
  return resolveMapStyle({
    storedMapStyle,
    savedMapStyle,
    commandCenterV3Enabled: commandCenterV3Enabled ?? v3TopPanelLayout,
  })
}

/**
 * @deprecated Use resolveMapStyle with commandCenterV3Enabled
 */
export function resolveLiveShipmentMapStyle({
  storedMapStyle,
  savedMapStyle,
  commandCenterV3Enabled = false,
} = {}) {
  return resolveMapStyle({
    storedMapStyle,
    savedMapStyle,
    commandCenterV3Enabled,
  })
}

export function resolveInitialMapView({
  savedMapStyle = null,
  commandCenterV3Enabled = false,
  v3TopPanelLayout,
} = {}) {
  return resolveMapStyle({
    storedMapStyle: readStoredMapStyle(),
    savedMapStyle,
    commandCenterV3Enabled: commandCenterV3Enabled ?? v3TopPanelLayout ?? false,
  })
}

export function resolveInitialLiveShipmentMapView({
  savedMapStyle = null,
  commandCenterV3Enabled = false,
} = {}) {
  return resolveInitialMapView({ savedMapStyle, commandCenterV3Enabled })
}
